import toast from 'react-hot-toast'

import { useMutation, useQuery, useQueryClient } from '@fv/client-core'
import { createBrandSchema } from '@fv/models'
import { vFile, vObject, type vOutput } from '@fv/models/validation'

import { supportMessage } from '@/constants'
import { shipperFetch, shipperFetchFormData } from '@/utils/shipperFetch'

import type { AccountBranding } from './types'

const queryKeys = {
  all: ['account-brands'],
  id: (id: string) => [...queryKeys.all, id],
}

const getAccountBrands = async () =>
  await shipperFetch<AccountBranding[]>(`/branding/`, {
    method: 'GET',
  })

export const useAccountBrands = () => {
  return useQuery({
    queryKey: queryKeys.all,
    queryFn: () => getAccountBrands(),
  })
}

export const useAccountBrand = (brandId?: string) => {
  const { data: brands } = useAccountBrands()
  return (
    brands?.find(brand => brand._id === brandId) ??
    brands?.find(brand => brand.isDefault)
  )
}

const removeBrand = async (id: string) =>
  await shipperFetch<AccountBranding>(`/branding/${id}`, {
    method: 'DELETE',
  })

export const useRemoveBrand = (id: string) => {
  const queryClient = useQueryClient()
  return useMutation(() => removeBrand(id), {
    onSuccess() {
      queryClient.setQueryData(queryKeys.all, (old: AccountBranding[]) =>
        old?.filter(b => b._id !== id),
      )
    },
  })
}

const addBrandSchema = vObject({
  ...createBrandSchema.entries,
  file: vFile(),
})

type AddBrandRequest = vOutput<typeof addBrandSchema>

const createBrand = async (dto: AddBrandRequest) => {
  const formData = new FormData()
  formData.append('file', dto.file)

  const query = new URLSearchParams({
    name: dto.name,
    isDefault: String(dto.isDefault ?? false),
  })

  return shipperFetchFormData<AccountBranding>(`/branding?${query}`, {
    method: 'POST',
    body: formData,
  })
}
export const useCreateBrand = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createBrand,
    onError() {
      toast.error(`Unable to create brand, ${supportMessage}`)
    },
    onSuccess(brand: AccountBranding) {
      queryClient.setQueryData(queryKeys.all, (old: AccountBranding[]) => [
        ...(old ?? []),
        brand,
      ])
      if (brand.isDefault) {
        queryClient.invalidateQueries({ queryKey: queryKeys.all })
      }
      toast.success('Brand created successfully')
    },
  })
}
